// The dashboard's role-scoped SURFACE model — the single in-code source for which surfaces exist, their
// nav grouping, and which role sees them. Derived from the surface→backend map (module-n-design-brief §2)
// and the N-* stories (spec/10-n-dashboard-ui §4); names follow spec/00-glossary.md.
//
// SCOPING NOTE (G-180, N-FDE-2 AC-6): the role scoping here decides what the NAV renders — it is NOT the
// security boundary. Every surface's data is authorised by the OWNING read interface server-side; hiding a
// nav item is never the control. The Operate group (Monitoring/Connectors/Capability map) is Operator-only
// in the nav, but an MD reaching those reads is scoped by the owning interface, not by this list.

/** The roles the dashboard renders for (identity's Role vocabulary; glossary). */
export type DashboardRole = 'admin_md' | 'operator';

/** A stable surface identifier — the client route key and the owning-module read it composes. */
export type SurfaceId =
  | 'work'
  | 'home'
  | 'tasks'
  | 'metrics'
  | 'scheduler'
  | 'approvals'
  | 'memory'
  | 'files'
  | 'team'
  | 'settings'
  | 'journeys'
  | 'monitoring'
  | 'connectors'
  | 'capmap'
  | 'governance';

/** A nav grouping heading (design brief: Work / Knowledge / Operate). */
export type NavGroup = 'Work' | 'Knowledge' | 'Operate';

/** One navigable surface: its id, human label, group, and which roles see it in the nav. */
export interface SurfaceDefinition {
  readonly id: SurfaceId;
  /** The nav LABEL shown to users. Note: `metrics` is LABELLED "KPIs" but the id/routes stay `metrics`
   *  (N-BOARD-2 AC-5 — KPI is a display synonym; the entity is always Metric). */
  readonly label: string;
  readonly group: NavGroup;
  /** The roles whose nav includes this surface. Operate surfaces are operator-only (design brief §2). */
  readonly roles: readonly DashboardRole[];
}

const BOTH: readonly DashboardRole[] = ['admin_md', 'operator'];
const OPERATOR_ONLY: readonly DashboardRole[] = ['operator'];

/** The single ordered surface registry. The nav is DERIVED from this — no second nav list exists. */
export const SURFACES: readonly SurfaceDefinition[] = [
  // The Work section (WS-J) leads the Work group — its own Workflow Definitions / boards / decision cards
  // (Ritona Work brief §5.1; WORK-DEC-001 (3)). Both roles see it; verdict controls render per `canRule` (G-180).
  { id: 'work', label: 'Workflows', group: 'Work', roles: BOTH },
  { id: 'home', label: 'Today', group: 'Work', roles: BOTH },
  { id: 'tasks', label: 'Tasks', group: 'Work', roles: BOTH },
  { id: 'metrics', label: 'KPIs', group: 'Work', roles: BOTH },
  { id: 'scheduler', label: 'Scheduler', group: 'Work', roles: BOTH },
  { id: 'approvals', label: 'Approvals', group: 'Work', roles: BOTH },
  { id: 'memory', label: 'Memory', group: 'Knowledge', roles: BOTH },
  { id: 'files', label: 'Files', group: 'Knowledge', roles: BOTH },
  { id: 'team', label: 'Team', group: 'Knowledge', roles: BOTH },
  // Journey Lab is operator oversight: it queues controlled scenario replay and exposes durable evidence.
  // The owning API remains the security boundary; the nav entry only keeps the affordance role-honest.
  { id: 'journeys', label: 'Journey Lab', group: 'Operate', roles: OPERATOR_ONLY },
  { id: 'monitoring', label: 'Monitoring', group: 'Operate', roles: OPERATOR_ONLY },
  { id: 'connectors', label: 'Connectors', group: 'Operate', roles: OPERATOR_ONLY },
  { id: 'capmap', label: 'Capability map', group: 'Operate', roles: OPERATOR_ONLY },
  { id: 'governance', label: 'Governance', group: 'Operate', roles: OPERATOR_ONLY },
];

/**
 * The SETTINGS surface is reached from the sidebar's account button (not a nav group), so it is defined here
 * as a standalone surface rather than in the grouped registry above. Keeping it out of `SURFACES` keeps the
 * nav groups exactly the prototype's Work / Knowledge / Operate.
 *
 * OPERATOR-ONLY (matches the owning read): the `/dashboard/api/settings` route is `requiredRole:'operator'`
 * (serve/api-routes.ts) — the deployment/config inventory + setup-readiness are an Operator concern, and an
 * ordinary member must not infer provider-provisioning state. Marking it operator-only here keeps the account
 * affordance HONEST (it never offers a member a surface the owning read would 403); it is not the boundary —
 * the owning read is (G-180).
 */
export const SETTINGS_SURFACE: SurfaceDefinition = {
  id: 'settings',
  label: 'Settings',
  // `group` is required by the type; settings is never rendered inside a nav group, so any group is inert.
  group: 'Work',
  roles: OPERATOR_ONLY,
};

/** The default surface when none is selected or the current one is out of the role's scope. */
export const DEFAULT_SURFACE: SurfaceId = 'home';

/** The ordered nav groups, in render order. */
export const NAV_GROUPS: readonly NavGroup[] = ['Work', 'Knowledge', 'Operate'];

/** The surfaces a given role sees, in registry order (the nav is generated from this — G-182 spirit). */
export function surfacesForRole(role: DashboardRole): readonly SurfaceDefinition[] {
  return SURFACES.filter((surface) => surface.roles.includes(role));
}

/** True when the role can reach the surface (nav groups + the account-button Settings surface). */
export function roleCanSeeSurface(role: DashboardRole, id: SurfaceId): boolean {
  if (id === SETTINGS_SURFACE.id) return SETTINGS_SURFACE.roles.includes(role);
  return surfacesForRole(role).some((surface) => surface.id === id);
}

/** Resolve a surface definition by id (undefined for an unknown id — the caller falls back to default). */
export function findSurface(id: string): SurfaceDefinition | undefined {
  if (id === SETTINGS_SURFACE.id) return SETTINGS_SURFACE;
  return SURFACES.find((surface) => surface.id === id);
}
